import React, { useState, useEffect, useRef } from 'react';
import { Document, Page } from 'react-pdf';
import ChatSidebar from './ChatSidebar';
import { sendChatMessage, getPdfUrl } from '../api';
import { pdfjs } from 'react-pdf';
import { useLocation, useNavigate } from 'react-router-dom';
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function Viewer({ viewMode, setViewMode }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [paperObj, setPaperObj] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [chatMessages, setChatMessages] = useState([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [ocrTextQP, setOcrTextQP] = useState('');
    const [ocrTextMS, setOcrTextMS] = useState('');
    const [ocrTextIN, setOcrTextIN] = useState('');
    const pdfViewerRef = useRef(null);
    const [pageWidth, setPageWidth] = useState(0);
    const [zoom, setZoom] = useState(0.9);
    const initialDistanceRef = useRef(null);
    const initialZoomRef = useRef(zoom);
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        let paperFromSource = null;
        let viewModeFromSource = null;

        if (location.state && location.state.paper) {
            paperFromSource = location.state.paper;
            viewModeFromSource = location.state.viewMode || 'qp';
            setPaperObj(paperFromSource);
            setViewMode(viewModeFromSource);
            const encodedPaper = encodeURIComponent(JSON.stringify(paperFromSource));
            if (params.get('paper') !== encodedPaper) {
                params.set('paper', encodedPaper);
            }
            if (viewModeFromSource) {
                params.set('viewMode', viewModeFromSource);
            }
            navigate(`?${params.toString()}`, { replace: true });
        } else if (params.get('paper')) {
            try {
                paperFromSource = JSON.parse(decodeURIComponent(params.get('paper')));
                viewModeFromSource = params.get('viewMode') || 'qp';
                setPaperObj(paperFromSource);
                setViewMode(viewModeFromSource);
            } catch (error) {
                console.error('Error parsing paper from URL:', error);
            }
        } 

        // NEW: Handle case when only filename is provided via state
        if (!paperFromSource && location.state && location.state.filename) {
            const filename = location.state.filename;
            viewModeFromSource = location.state.viewMode || 'qp';
            paperFromSource = { qp: filename, ms: filename, in: filename };
            setPaperObj(paperFromSource);
            setViewMode(viewModeFromSource);
            params.set('filename', filename);
            if (viewModeFromSource) {
                params.set('viewMode', viewModeFromSource);
            }
            navigate(`?${params.toString()}`, { replace: true });
        } else if (!paperFromSource && params.get('filename')) {
            const filename = params.get('filename');
            viewModeFromSource = params.get('viewMode') || 'qp';
            paperFromSource = { qp: filename, ms: filename, in: filename };
            setPaperObj(paperFromSource);
            setViewMode(viewModeFromSource);
        }

        if (!paperFromSource) {
            navigate('/');
        }
    }, [location.state, location.search, navigate]);

    // New function to extract OCR from a PDF and upload them to the backend, returning the OCR text.
    async function extractOCRFromUrl(url) {
        try {
            const loadingTask = pdfjs.getDocument(url);
            const pdf = await loadingTask.promise;
            let ocrText = "";
            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const textContent = await page.getTextContent();
                const pageText = textContent.items.map(item => item.str).join(' ');
                ocrText += `Page ${i}: ${pageText}\n`;
            }
            return ocrText;
        } catch (error) {
            console.error('Error extracting OCR from', url, error);
            return 'OCR not available';
        }
    }

    // useEffect to extract OCR text for all relevant documents
    useEffect(() => {
        if (paperObj) {
            if (paperObj.qp) {
                extractOCRFromUrl(getPdfUrl(paperObj.qp)).then(data => setOcrTextQP(data));
            } else {
                setOcrTextQP('Not available');
            }
            if (paperObj.ms) {
                extractOCRFromUrl(getPdfUrl(paperObj.ms)).then(data => setOcrTextMS(data));
            } else {
                setOcrTextMS('Not available');
            }
            if (paperObj.in) {
                extractOCRFromUrl(getPdfUrl(paperObj.in)).then(data => setOcrTextIN(data));
            } else {
                setOcrTextIN('Not available');
            }
        }
    }, [paperObj]);

    // Updated onDocumentLoadSuccess to recalc pageWidth immediately
    function onDocumentLoadSuccess(pdf) {
        setNumPages(pdf.numPages);
        if (pdfViewerRef.current) {
            setPageWidth(pdfViewerRef.current.clientWidth);
        }
    }

    // Updated AI message handler that prepends paper metadata, document URLs and OCR data as a system message
    const handleAIMessage = async (contextMessages) => {
        setIsStreaming(true);
        let messagesToSend = contextMessages;
        if (!messagesToSend.some(m => m.role === 'system' && ((typeof m.content === 'string' && m.content.startsWith('Paper:')) || (Array.isArray(m.content) && m.content[0].text.startsWith('Paper:'))))) {
            messagesToSend = [{
                role: 'system',
                content: [
                    { type: 'text', text: `Paper: ${paperObj.code}, ${paperObj.name}.` },
                    { type: 'text', text: 'Documents URLs:\n' },
                    { type: 'text', text: `Question Paper: ${paperObj.qp || 'N/A'}\n` },
                    { type: 'text', text: `Marking Scheme: ${paperObj.ms || 'N/A'}\n` },
                    { type: 'text', text: `Reading Insert: ${paperObj.in || 'N/A'}\n\n` },
                    { type: 'text', text: 'Note: The Question Paper contains the questions; the Marking Scheme contains the answers; the Reading Insert provides additional context for answering the questions.\n\n' },
                    { type: 'text', text: 'Document OCR Data:\n' },
                    { type: 'text', text: 'Question Paper OCR:\n' },
                    paperObj.qp ? { type: 'text', text: ocrTextQP } : { type: 'text', text: 'Not available' },
                    { type: 'text', text: 'Marking Scheme OCR:\n' },
                    paperObj.ms ? { type: 'text', text: ocrTextMS } : { type: 'text', text: 'Not available' },
                    { type: 'text', text: 'Reading Insert OCR:\n' },
                    paperObj.in ? { type: 'text', text: ocrTextIN } : { type: 'text', text: 'Not available' }
                ]
            }, ...messagesToSend];
        }
        const updatedMessages = [...messagesToSend, { role: 'assistant', content: '' }];
        setChatMessages(updatedMessages);
        
        await sendChatMessage(updatedMessages, (data) => {
            const incomingMessage = data.message;

            if (incomingMessage && incomingMessage.content && incomingMessage.role === 'assistant') {
                setChatMessages(prev => {
                    const lastIndex = prev.length - 1;
                    const lastMessage = prev[lastIndex];
                    if (lastMessage.role !== 'assistant') return prev;

                    const updatedMessage = {
                        ...lastMessage,
                        content: (lastMessage.content || '') + incomingMessage.content
                    };

                    return [...prev.slice(0, lastIndex), updatedMessage];
                });
            }

            if (data.done === true || data.done_reason || (incomingMessage && incomingMessage.content === "[DONE]")) {
                setIsStreaming(false);
            }
        });
    };

    const getFilePath = (mode) => {
        if (!paperObj || !paperObj[mode]) {
            console.error(`Paper or paper file for mode ${mode} is undefined`);
            return '';
        }
        return paperObj[mode];
    };

    useEffect(() => {
        function handleResize() {
            if (pdfViewerRef.current) {
                setPageWidth(pdfViewerRef.current.clientWidth);
            }
        }
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.1, 3));
    const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.5));
    const handleResetZoom = () => setZoom(0.9);
    const handleDownload = () => {
        const fileUrl = getPdfUrl(getFilePath(viewMode));
        const link = document.createElement('a');
        link.href = fileUrl;
        // Use paperObj.code if available, else default name
        link.download = (paperObj && paperObj.code ? paperObj.code : 'document') + '.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else if(window.opener) {
            window.close();
        } else {
            navigate('/');
        }
    };

    useEffect(() => {
        const viewer = pdfViewerRef.current;
        if (!viewer) return;
        const getDistance = (touch1, touch2) => {
            const dx = touch2.pageX - touch1.pageX;
            const dy = touch2.pageY - touch1.pageY;
            return Math.sqrt(dx * dx + dy * dy);
        };
        
        const handleTouchStart = (e) => {
            if (e.touches.length === 2) {
                initialDistanceRef.current = getDistance(e.touches[0], e.touches[1]);
                initialZoomRef.current = zoom; // capture current zoom
            }
        };
        
        const handleTouchMove = (e) => {
            if (e.touches.length === 2 && initialDistanceRef.current) {
                const currentDistance = getDistance(e.touches[0], e.touches[1]);
                const scaleFactor = currentDistance / initialDistanceRef.current;
                setZoom(Math.min(Math.max(initialZoomRef.current * scaleFactor, 0.5), 3));
            }
        };
        
        const handleTouchEnd = (e) => {
            if (e.touches.length < 2) {
                initialDistanceRef.current = null;
            }
        };
        
        viewer.addEventListener('touchstart', handleTouchStart);
        viewer.addEventListener('touchmove', handleTouchMove);
        viewer.addEventListener('touchend', handleTouchEnd);
        viewer.addEventListener('touchcancel', handleTouchEnd);
        
        return () => {
            viewer.removeEventListener('touchstart', handleTouchStart);
            viewer.removeEventListener('touchmove', handleTouchMove);
            viewer.removeEventListener('touchend', handleTouchEnd);
            viewer.removeEventListener('touchcancel', handleTouchEnd);
        };
    }, [zoom]);

    return !paperObj ? null : (
        <div className="viewer-wrapper" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            <div className="viewer-navbar">
                <select value={viewMode} onChange={(e) => {
                    const newMode = e.target.value;
                    const params = new URLSearchParams();
                    params.set('paper', encodeURIComponent(JSON.stringify(paperObj)));
                    params.set('viewMode', newMode);
                    const newUrl = `${window.location.pathname}?${params.toString()}`;
                    window.open(newUrl, '_blank');
                }}>
                    <option value="qp">Question Paper</option>
                    <option value="ms">Marking Scheme</option>
                    {paperObj.in && <option value="in">Reading Insert</option>}
                </select>
                {/* <button onClick={() => setSidebarOpen(!sidebarOpen)}>
                    {sidebarOpen ? 'Close Chat' : 'Open Chat'}
                </button> */}
                <button onClick={handleBack}>Back</button>
                {!isMobile && (
                    <>
                        <button onClick={handleZoomOut}>-</button>
                        <button onClick={handleZoomIn}>+</button>
                        <button onClick={handleResetZoom}>Reset</button>
                    </>
                )}
                <button onClick={handleDownload}>Download</button>
            </div>
            <div className="viewer-body" style={{ display: 'flex', flex: 1 }}>
                <div className="pdf-viewer" style={{ flex: 1, height: '100%' }} ref={pdfViewerRef}>
                    <Document
                        file={getPdfUrl(getFilePath(viewMode))}
                        onLoadSuccess={onDocumentLoadSuccess}
                    >
                        {Array.from(new Array(numPages), (el, index) => (
                            <Page key={`page_${index + 1}`} pageNumber={index + 1} width={pageWidth * zoom} renderTextLayer={true} style={{ display: 'block', marginBottom: 0 }} />
                        ))}
                    </Document>
                </div>
                {/* {sidebarOpen && (
                    <ChatSidebar
                        messages={chatMessages}
                        onSendMessage={async (message) => {
                            const userMessage = { role: 'user', content: message };
                            const updatedMessages = [...chatMessages, userMessage];
                            setChatMessages(updatedMessages);
                            await handleAIMessage(updatedMessages);
                        }}
                        isStreaming={isStreaming}
                    />
                )} */}
            </div>
        </div>
    );
}